import type { TileSet } from './tiles';
import { pairs, type WorldMapSpec } from './worldSpec';

/** Wonderfizz Works' painting (approved 29 October), tiled as public/world/candy/v1
 *  at WebP Q78 (the highest at which the opening view stays inside its 1.5 MB
 *  budget measured both ways: levels 0 to 2 in full plus the level-3 columns of the
 *  view, 1,281 KB at 390x844 and 1,504 KB at 375x667; and tile-map.sh's own worst
 *  case, 1,148 KB. Q79 is 1,322, 1,554 and 1,187 KB, over at 375x667). The world's key
 *  in code is `candy` (the biome id); its approved art folder is `wonderfizz`. A
 *  changed painting goes in a new version folder, with its spots measured again. */
export const CANDY_TILES: TileSet = {
  base: '/world/candy/v1/map_files',
  width: 3072,
  height: 2048,
  tileSize: 512,
  top: 3,
};

/**
 * Wonderfizz Works, from the painting's spots.json: pink frosting hills, cotton-candy
 * trees and the cocoa river, its seven buildings along a candy-striped road (node ids
 * 43 to 49, the world's slots in order). The lollipop cottages', the factory's, the
 * pump house's and the vault's outlines hug thin structures (the giant lollipops, the
 * chimneys, the candy-cane pipes, the crystals), so those four carry a padded `tap`
 * shape a finger can hit; the others are tapped by their outlines. The vault's `tap` runs
 * to the painting's right edge (the art's has its two right-hand corners 2 px short of
 * the outline's). The painting's two arched bridges are still areas (the river's ripple
 * and the pieces keep off them). The mill's waterfall (its chute and foam, the art's
 * world.json) is already cut out of the mill's outline in spots.json: the chute falls,
 * the foam flows with the river and no piece enters either.
 *
 * The road is the painted candy-striped path, 50 px across: it crosses the river by the
 * lower bridge (between road points 15 and 18, a still area) on the two short striped
 * paths added after the composition (Jon kept them). Its band touches 547 px of the
 * water mask on the west bank (the lit stripe's edge), accepted.
 *
 * Every plate hangs where the painting leaves room beside or above its building (Jon,
 * 26 October), clear of every outline, the still areas, the pieces' places and the road,
 * and where a phone's view reaches it with the building in the middle of the screen (the
 * top bar and the button columns hide a plate they cover). The five buildings that stand
 * close to the road (Sherbet Square, the mill, the factory, the pump house, the vault)
 * hang theirs above or beside them, none below: the mill's is beside it, to its right
 * past the waterfall. The mill and the pump house carry a narrow two-line plate on the
 * smallest phone, where a wide one cannot clear their neighbours. There is no painted
 * lamp along the road, so the posts the code draws stand on open ground beside it: 14,
 * from world.json `lanterns_sim.clear_spots` as a start, seven of which a 2x crop at
 * every foot (31 October) showed in a shrub, on the river's rocks or against a dome, so
 * those seven moved (by 15 to 90 px) to open pink flat or grass: off the water, the
 * trunks, the shrubs, the jelly domes, the curbs, the striped path's edge, every plate
 * and the pieces' places.
 */
export const CANDY: WorldMapSpec = {
  biome: 'candy',
  tiles: CANDY_TILES,
  buildings: [
    {
      node: 43,
      name: 'Sherbet Square',
      centre: [300, 960],
      plate: [300, 572],
      plateTight: [284, 550],
      outline: pairs([
        92, 880, 120, 810, 190, 770, 210, 740, 270, 700, 300, 690, 340, 710,
        400, 700, 430, 696, 450, 750, 490, 790, 496, 880, 504, 940, 544, 1000,
        556, 1080, 536, 1150, 480, 1196, 380, 1216, 260, 1212, 200, 1170, 180, 1110,
        150, 1060, 100, 1030, 92, 960,
      ]),
    },
    {
      node: 44,
      name: 'Lollipop Lane',
      centre: [840, 660],
      plate: [820, 186],
      plateTight: [820, 150],
      outline: pairs([
        566, 430, 600, 370, 680, 352, 724, 410, 732, 480, 766, 464, 780, 424,
        840, 416, 850, 320, 900, 306, 956, 330, 964, 410, 1000, 406, 1050, 430,
        1056, 510, 1044, 580, 1030, 660, 1044, 730, 1000, 770, 936, 790, 880, 816,
        800, 824, 720, 830, 660, 816, 646, 740, 664, 660, 676, 570, 650, 524,
        584, 510,
      ]),
      tap: pairs([
        968, 314, 896, 286, 614, 346, 580, 362, 546, 424, 648, 832, 716, 850,
        882, 836, 1012, 786, 1062, 740, 1076, 534, 1070, 424,
      ]),
    },
    {
      node: 45,
      name: 'Cocoa River',
      centre: [1060, 1020],
      plate: [1530, 1146],
      plateTight: [1520, 1116],
      plateNarrow: true,
      outline: pairs([
        1040, 816, 997, 863, 942, 908, 889, 962, 864, 1039, 864, 1121, 890, 1180,
        979, 1210, 1085, 1221, 1148, 1239, 1169, 1255, 1172, 1245, 1185, 1242, 1159, 1187,
        1130, 1142, 1126, 1126, 1129, 1122, 1159, 1115, 1194, 1131, 1222, 1174, 1240, 1236,
        1262, 1239, 1292, 1256, 1310, 1219, 1281, 1122, 1256, 1080, 1250, 979, 1230, 900,
        1179, 839, 1120, 800,
      ]),
    },
    {
      node: 46,
      name: 'Gummy Lab',
      centre: [1484, 800],
      plate: [1484, 396],
      plateTight: [1448, 370],
      outline: pairs([
        1270, 720, 1300, 640, 1336, 570, 1400, 536, 1460, 516, 1540, 524, 1600, 550,
        1644, 600, 1652, 700, 1690, 730, 1700, 840, 1710, 910, 1700, 980, 1660, 1000,
        1580, 1010, 1500, 996, 1400, 990, 1344, 976, 1300, 940, 1284, 880, 1270, 800,
      ]),
    },
    {
      node: 47,
      name: 'Fizz Factory',
      centre: [2000, 840],
      plate: [2250, 376],
      plateTight: [2248, 352],
      outline: pairs([
        1830, 760, 1844, 640, 1860, 606, 1904, 606, 1924, 580, 1924, 510, 1950, 496,
        1984, 504, 1994, 580, 2016, 600, 2024, 564, 2060, 570, 2080, 600, 2084, 690,
        2116, 690, 2140, 716, 2180, 744, 2212, 790, 2212, 940, 2192, 1040, 2156, 1090,
        2100, 1124, 2000, 1132, 1880, 1132, 1816, 1096, 1796, 1000, 1810, 880,
      ]),
      tap: pairs([
        1950, 476, 1904, 502, 1824, 632, 1776, 986, 1794, 1096, 1806, 1114, 1880, 1152,
        2100, 1144, 2166, 1108, 2212, 1046, 2232, 950, 2232, 784, 2080, 562, 1996, 488,
      ]),
    },
    {
      node: 48,
      name: 'Peppermint Pipeworks',
      centre: [2384, 1130],
      plate: [2455, 738],
      plateTight: [2460, 712],
      plateNarrow: true,
      outline: pairs([
        2184, 1204, 2200, 1132, 2240, 1056, 2284, 976, 2336, 890, 2400, 876, 2450, 940,
        2500, 910, 2550, 940, 2570, 1040, 2600, 1120, 2596, 1220, 2560, 1300, 2510, 1370,
        2410, 1384, 2320, 1380, 2220, 1340, 2170, 1280,
      ]),
      tap: pairs([
        2402, 856, 2334, 870, 2316, 882, 2180, 1126, 2150, 1270, 2156, 1296, 2208, 1356,
        2318, 1400, 2416, 1404, 2522, 1386, 2576, 1314, 2616, 1228, 2620, 1112, 2568, 930,
        2504, 890,
      ]),
    },
    {
      node: 49,
      name: 'Sugarplum Vault',
      centre: [2840, 980],
      plate: [2860, 560],
      plateTight: [2800, 520],
      outline: pairs([
        2624, 1040, 2660, 940, 2690, 860, 2770, 720, 2860, 690, 2940, 680, 3040, 700,
        3072, 700, 3072, 1220, 3000, 1230, 2900, 1224, 2780, 1210, 2680, 1180, 2636, 1120,
      ]),
      tap: pairs([
        3072, 680, 2934, 660, 2850, 672, 2750, 712, 2666, 862, 2604, 1032, 2616, 1126,
        2668, 1196, 2778, 1230, 3006, 1250, 3072, 1240, 3072, 1220, 3072, 700,
      ]),
    },
  ],
  road: pairs([
    0, 1110, 87, 1137, 194, 1201, 300, 1307, 450, 1336, 577, 1316,
    700, 1320, 770, 1296, 862, 1314, 1000, 1328, 1100, 1326, 1170, 1341,
    1252, 1390, 1310, 1421, 1380, 1478, 1432, 1536, 1580, 1588, 1700, 1600,
    1778, 1556, 1768, 1506, 1755, 1456, 1762, 1398, 1810, 1352, 1950, 1303,
    2030, 1283, 2164, 1353, 2247, 1434, 2324, 1463, 2450, 1462, 2574, 1404,
    2679, 1356, 2775, 1330, 2872, 1316, 2977, 1285, 3072, 1258,
  ]),
  roadWidth: 50,
  still: [
    {
      name: 'stone and candy bridge, upper',
      outline: pairs([
        1450, 140, 1470, 116, 1520, 104, 1580, 104, 1660, 120, 1720, 116, 1760, 120,
        1790, 150, 1790, 200, 1760, 224, 1700, 230, 1640, 236, 1580, 244, 1530, 230,
        1490, 210, 1456, 184,
      ]),
    },
    {
      name: 'stone and candy bridge, lower',
      outline: pairs([
        1420, 1580, 1460, 1550, 1540, 1536, 1640, 1532, 1740, 1532, 1810, 1536, 1844, 1570,
        1850, 1624, 1830, 1660, 1760, 1676, 1680, 1670, 1600, 1656, 1520, 1650, 1470, 1630,
        1430, 1610,
      ]),
    },
  ],
  lanternPosts: pairs([
    131, 1106, 366, 1278, 532, 1365, 777, 1388, 1100, 1358, 1289, 1462,
    1822, 1476, 1748, 1355, 1939, 1350, 2104, 1275, 2376, 1422, 2530, 1468,
    2604, 1339, 2865, 1358,
  ]),
  plateBox: [420, 110],
  plateBoxTight: [528, 138],
  plateBoxNarrow: [394, 138],
  ambient: '/world/candy/ambient/v1',
  ambientFiles: ['life.json', 'life.webp', 'sway.webp', 'water.webp'],
  // Wonderfizz Works' road friends' kinds: no sugar mice, gingerbread folk, gummy bears
  // (outside the lab's jar), unicorns or horned ponies, and no tall chef's hats (the sweet
  // seller, confectioners in low caps and candy-coloured birds stand in).
  forbidden: ['mouse', 'mice', 'gingerbread', 'gummy', 'bear', 'unicorn', 'pony', 'ponies', 'chef'],
};
